import React, { useState, useEffect } from "react";
import { notification } from "antd";
import { SentinelService } from "../../services/SentinelService";
import { Sentinel } from "../../model_gen/sentinel";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import EditSentinelForm from "./EditSentinelForm";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { ArrowLeftIcon } from "lucide-react";

const EditSentinel: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const { sentinelId, intentGroupId } = useParams();
  const [loading, setLoading] = useState(false);
  const [sentinel, setSentinel] = useState<Sentinel | undefined>();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (sentinelId) {
      setLoading(true);
      SentinelService.getSentinel(
        Number(sentinelId),
        setLoading,
        apiKey
      ).subscribe(
        (sentinel: Sentinel) => {
          setSentinel(sentinel);
          setLoading(false);
          setIsDataLoaded(true);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to fetch sentinel",
          });
          setLoading(false);
        }
      );
    }
  }, [sentinelId, apiKey]);

  const handleSubmit = (formData: Sentinel) => {
    setLoading(true);
    SentinelService.updateSentinel(
      formData,
      setLoading,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Success",
          description: "Sentinel updated successfully",
        });
        navigate(-1);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to update sentinel",
        });
        setLoading(false);
      }
    );
  };

  const handleClose = () => {
    navigate(-1);
  };

  if (loading && !isDataLoaded) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-10 bg-muted rounded w-full animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${intentGroupId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Group
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Edit Sentinel</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Edit Sentinel
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Update sentinel configuration and monitoring rules
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleClose}
                className="gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <EditSentinelForm
              sentinel={sentinel}
              loading={loading}
              setLoading={setLoading}
              isDataLoaded={isDataLoaded}
              onSubmit={handleSubmit}
              onClose={handleClose}
              regenerationEnabled={true}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditSentinel;
